import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, Warning, ShieldCheck, ArrowRight } from "@phosphor-icons/react";

// Human-readable scope descriptions per integration.
const SCOPE_COPY = {
  gmail: {
    reads: ["Read messages in your inbox", "See sender, subject and preview text"],
    never: ["Send any email without your explicit approval", "Delete or archive any message", "Read messages older than 30 days by default"],
  },
  google_calendar: {
    reads: ["See your upcoming events and attendees", "Check for scheduling conflicts"],
    never: ["Create or modify events without approval", "Cancel meetings", "Share your calendar externally"],
  },
  google_bundle: {
    reads: ["Read messages in your Gmail inbox", "See sender, subject and preview text", "See your upcoming Calendar events and attendees"],
    never: ["Send email or messages without approval", "Delete or modify anything", "Share data with anyone but you"],
  },
  outlook: {
    reads: ["Read messages in your inbox", "See upcoming calendar events"],
    never: ["Send email without your explicit approval", "Delete or modify anything", "Access files in OneDrive"],
  },
  slack: {
    reads: ["Read message history in channels you're in", "See channel names and members"],
    never: ["Post messages without your explicit approval", "Read direct messages you're not part of", "Delete anything"],
  },
  hubspot: {
    reads: ["Read your contacts and their properties", "Read deal stages and amounts"],
    never: ["Modify contacts without approval", "Send emails via HubSpot", "Delete or export data"],
  },
};

const NICE_LABEL = {
  gmail: "Gmail", google_calendar: "Google Calendar", google_bundle: "Google (Gmail + Calendar)",
  outlook: "Outlook / M365", slack: "Slack", hubspot: "HubSpot",
};

export default function ConsentDialog({ open, onOpenChange, integration, onContinue }) {
  const copy = SCOPE_COPY[integration];
  if (!copy) return null;
  const label = NICE_LABEL[integration] || integration;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="consent-dialog"
        className="max-w-lg bg-[#1c1917] border border-stone-800 text-stone-100 rounded-none p-0 gap-0"
      >
        <DialogHeader className="px-8 pt-8 pb-6 border-b border-stone-800">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-orange-500/80">
            <ShieldCheck size={14} weight="duotone" /> Before you continue
          </div>
          <DialogTitle asChild>
            <h2 className="font-serif text-3xl mt-2 tracking-tight text-stone-50" data-testid="consent-title">
              Copilot will connect to {label}
            </h2>
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-400 mt-2 leading-relaxed">
            You&apos;re about to be sent to {label} to authorise Copilot. Here&apos;s exactly what changes — and what doesn&apos;t — after you click Allow.
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6 space-y-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-green-500/80 mb-3 flex items-center gap-1.5">
              <Check size={12} weight="bold" /> What Copilot will do
            </div>
            <ul className="space-y-2 text-sm text-stone-200" data-testid="consent-reads">
              {copy.reads.map((r, i) => (
                <li key={i} className="flex gap-2"><span className="text-green-500 mt-0.5">•</span><span>{r}</span></li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-500/80 mb-3 flex items-center gap-1.5">
              <Warning size={12} weight="bold" /> What Copilot will never do
            </div>
            <ul className="space-y-2 text-sm text-stone-300" data-testid="consent-nevers">
              {copy.never.map((n, i) => (
                <li key={i} className="flex gap-2"><span className="text-amber-500 mt-0.5">•</span><span>{n}</span></li>
              ))}
            </ul>
          </div>

          <div className="text-[11px] font-mono text-stone-500 border-t border-stone-800 pt-4 leading-relaxed">
            High-stakes actions (sending email, sending messages, spending money) always require your explicit Yes in the app or on Telegram. You can revoke access from the Integrations page any time.
          </div>
        </div>

        <DialogFooter className="px-8 py-5 border-t border-stone-800 flex flex-row gap-2">
          <button
            data-testid="consent-cancel"
            onClick={() => onOpenChange(false)}
            className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 border border-stone-700 text-stone-300 hover:border-stone-500 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="consent-continue"
            onClick={() => { onOpenChange(false); onContinue?.(); }}
            className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white transition-colors flex items-center gap-2"
          >
            Continue to {label} <ArrowRight size={12} weight="bold" />
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
